import { useState, useEffect, useRef, useLayoutEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { AppLink } from '@/shared/components/AppLink';
import { ChevronDown, ArrowRight } from 'lucide-react';
import { useDesktopMenu, withLeadingSlash } from '@/shared/features/menu/presentation/hooks/useDesktopMenu';
import { Button } from '@/shared/widgets/button';
import cutIcon from '@/shared/assets/icons/cut.svg';
import colorIcon from '@/shared/assets/icons/color.svg';
import clarityIcon from '@/shared/assets/icons/clarity.svg';
import caratIcon from '@/shared/assets/icons/carat.svg';
import tipsIcon from '@/shared/assets/icons/tips.svg';
import certificationIcon from '@/shared/assets/icons/certification.svg';
import { useStickyOnScroll } from '@/core/hooks/useStickyOnScroll';
import { useScrollDirection } from '@/core/hooks/useScrollDirection';
import { isPlpRoute } from '@/features/plp/utils/plpUrl';
import { useAppSelector } from '@/app/store/store';
import { useMultivariateExperiment } from '@/shared/features/multivariateexperiment/presentation/hooks/useMultivariateExperiment';
import { selectDesktopRecommendedBrowseFilterStripStuck } from '@/app/store/newHomeUiSlice';
import { readRecommendedCategoryFilterStripStuckFromDocument } from '@/features/newHome/utils/recommendedCategoryStripSentinel';

function navShouldRefresh(url: string | undefined): boolean {
  if (!url || url === '#') return true;
  const path = url.split('?')[0];
  // Gold coin pages are served outside the React app — always do a full refresh.
  if (/goldcoin/i.test(path) || path.toLowerCase().startsWith('/gold+coins/') || path.toLowerCase().startsWith('/gold%2Bcoins/')) return true;
  return !isPlpRoute(path) && path !== '/';
}

// Diamond Education icons mapping
const diamondEducationIcons: Record<string, string> = {
  'Cut': cutIcon,
  'Colour': colorIcon,
  'Color': colorIcon,
  'Clarity': clarityIcon,
  'Carat': caratIcon,
  'Tips & Tricks': tipsIcon,
  'Certification': certificationIcon,
};

function BsLogoSymbolIcon({ color }: { color: string }) {
  return (
    <svg aria-hidden viewBox="0 0 29 32" className="h-full w-full" style={{ color }}>
      <path d="M20.61 26.602c-1.192 0-2.159 0.97-2.159 2.165s0.967 2.166 2.159 2.166c0.13 0 0.256-0.014 0.379-0.035 0.434-0.078 0.822-0.283 1.125-0.578 0.404-0.394 0.656-0.943 0.656-1.553 0-1.196-0.967-2.165-2.159-2.165z" fill="currentColor" />
      <path d="M16.124 7.861c-3.291-3.727-1.81-6.713-0.923-7.861-3.23 1.442-6.516 5.863-1.386 11.82 0 0 0.45 0.523 1.206 1.401 2.387 2.774 7.821 9.092 11.767 13.704 0.152-0.173 0.301-0.349 0.447-0.527 1.353-1.661 1.325-4.055-0.071-5.68l-11.040-12.857z" fill="currentColor" />
      <path d="M26.311 4.638c-0.207-0.804-0.663-1.601-1.201-2.236-1.574-1.856-4.738-2.402-4.738-2.402s0.637 0.364 1.292 1.134c0.828 0.974 1.608 2.58 0.846 4.776-0.152 0.468-0.39 0.919-0.39 1.422 0 1.14 0.921 2.063 2.057 2.063 0.332 0 0.663-0.081 0.957-0.236 0.249-0.132 0.46-0.308 0.636-0.514 0.397-0.465 0.616-1.083 0.697-1.687 0.105-0.772 0.038-1.569-0.156-2.321z" fill="currentColor" />
      <path d="M11.499 25.019c-0.358 2.813-1.907 5.178-4.071 6.661 0.407 0.083 0.818 0.129 1.232 0.129 3.657 0 6.381-2.747 7.217-5.941 0.032-0.121 0.061-0.243 0.087-0.366 0.012-0.053 0.023-0.106 0.034-0.159 0.029-0.152 0.055-0.305 0.076-0.46 0.008-0.064 0.016-0.128 0.023-0.192 0.008-0.067 0.015-0.134 0.020-0.2 0.005-0.057 0.008-0.114 0.012-0.171 0.005-0.077 0.009-0.153 0.012-0.228 0.002-0.056 0.003-0.111 0.004-0.166 0.059-4.022-2.901-7.532-6.973-8.054-0.634-0.081-1.259-0.083-1.868-0.017 2.929 1.94 4.67 5.438 4.196 9.166z" fill="currentColor" />
      <path d="M4.318 30.101v-25.089c-1.857 1.481-3.348 3.402-4.318 5.607v15.713c0.322 0.384 2.044 2.37 4.318 3.769z" fill="currentColor" />
    </svg>
  );
}
import { AnalyticsBannerLink } from '@/shared/features/analytics/presentation/components';
import {
  BROWSE_ALL_COLLECTIONS_CLICKED,
  BROWSE_ALL_COLLECTIONS_FROM_SOURCE_NAV_BAR,
  browseAllCollectionsClickedEventProperties,
  formatNavMenuIdForCategory,
} from '@/shared/features/analytics/utils/browseAllCollectionsClicked';
import type { DesktopNavItem, DesktopNavColumnItem, DesktopNavSubGroup } from '../../domain/models/DesktopNavItem';
import { headerLayoutWrapperClass, useDesktopHeaderConfig } from '@/shared/features/header/presentation/hooks/useDesktopHeaderConfig';
import { cn } from '@/shared/utils/cn';
import { useTrackNavigationBarHovered } from '@/shared/features/menu/presentation/hooks/useTrackNavigationBarHovered';
import { useTrackNavigationBarClick } from '@/shared/features/menu/presentation/hooks/useTrackNavigationBarClick';
import {
  NAVIGATION_BAR_CLICKED,
  navigationBarClickedEventProperties,
  type NavigationBarClickContext,
} from '@/shared/features/analytics/utils/navigationBarClicked';

function navBarLinkTrack(ctx: NavigationBarClickContext) {
  return {
    eventName: NAVIGATION_BAR_CLICKED,
    eventProperties: navigationBarClickedEventProperties(ctx),
  } as const;
}

/** New home recommended filter strip: delay dropping `sticky` until slide-up finishes */
const NAV_HIDE_FOR_FILTER_STRIP_MS = 380;

export default function DesktopNavbar({ className }: { className?: string }) {
  const [hoveredMenu, setHoveredMenu] = useState<string | null>(null);
  const { sentinelRef, isSticky } = useStickyOnScroll();
  const { desktopMenuData, isLoading: menuLoading } = useDesktopMenu();
  const location = useLocation();

  const pathNoQuery = location.pathname.split('?')[0];
  const { homePageVariant: homeVariant } = useMultivariateExperiment();
  const isNewHomeRecommendedBrowse =
    (homeVariant === 'V2' || homeVariant === 'V3' || homeVariant === 'V4' || homeVariant === 'V5' || homeVariant === 'V6' || homeVariant === 'V7') && (pathNoQuery === '/' || pathNoQuery === '');
  const scrollDir = useScrollDirection();
  const desktopRecommendedFilterStripStuck = useAppSelector(
    selectDesktopRecommendedBrowseFilterStripStuck,
  );

  const [recommendedFilterStripStuckFromDom, setRecommendedFilterStripStuckFromDom] = useState(false);

  const syncRecommendedStripStuckFromDom = useCallback(() => {
    if (!isNewHomeRecommendedBrowse) {
      setRecommendedFilterStripStuckFromDom(false);
      return;
    }
    setRecommendedFilterStripStuckFromDom(readRecommendedCategoryFilterStripStuckFromDocument());
  }, [isNewHomeRecommendedBrowse]);

  useLayoutEffect(() => {
    const id = window.requestAnimationFrame(() => {
      syncRecommendedStripStuckFromDom();
      window.requestAnimationFrame(() => syncRecommendedStripStuckFromDom());
    });
    return () => window.cancelAnimationFrame(id);
  }, [location.pathname, syncRecommendedStripStuckFromDom]);

  useEffect(() => {
    if (!isNewHomeRecommendedBrowse) return;
    const run = () => syncRecommendedStripStuckFromDom();
    run();
    window.addEventListener('scroll', run, { passive: true });
    window.addEventListener('resize', run);
    const onLoad = () =>
      window.requestAnimationFrame(() => window.requestAnimationFrame(run));
    if (document.readyState === 'complete') onLoad();
    else window.addEventListener('load', onLoad, { once: true });
    const onPageShow = (e: PageTransitionEvent) => {
      if (e.persisted) window.requestAnimationFrame(run);
    };
    window.addEventListener('pageshow', onPageShow);
    return () => {
      window.removeEventListener('scroll', run);
      window.removeEventListener('resize', run);
      window.removeEventListener('pageshow', onPageShow);
    };
  }, [isNewHomeRecommendedBrowse, syncRecommendedStripStuckFromDom]);

  useEffect(() => {
    if (!isNewHomeRecommendedBrowse) return;
    let cancelled = false;
    let frames = 0;
    const burst = () => {
      if (cancelled) return;
      syncRecommendedStripStuckFromDom();
      frames += 1;
      if (frames < 24) requestAnimationFrame(burst);
    };
    requestAnimationFrame(burst);
    return () => {
      cancelled = true;
    };
  }, [isNewHomeRecommendedBrowse, location.pathname, syncRecommendedStripStuckFromDom]);

  // Dismiss any open submenu when the user navigates to a new page.
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- dismissing stale hover state on navigation; no external system involved
    setHoveredMenu(null);
  }, [location.pathname]);

  // On PLP/search pages the filter bar owns top-0 when scrolling down.
  // Navbar is sticky only when scrolling up (mirrors mobile header behaviour).
  const p = location.pathname.split('?')[0];
  const isPlpPage =
    p.startsWith('/jewellery') ||
    p.startsWith('/search') ||
    p.startsWith('/plp') ||
    /^\/stores\/[^/]+\/catalog/.test(p);
  const plpStickyEligible = !isPlpPage || scrollDir === 'up';

  // When the recommended-section filter strip is stuck, always hide the navbar
  // (on both scroll-down and scroll-up).  On scroll-up the NewHomeHeader fixed
  // wrapper shows the desktop header above the filter strip instead.
  const hideNavbarForFilter =
    isNewHomeRecommendedBrowse &&
    (recommendedFilterStripStuckFromDom || desktopRecommendedFilterStripStuck);

  /** While true (first ~380ms after filter strip sticks), keep `sticky` and animate `translateY` up; then false so navbar releases like before. */
  const [allowNavSticky, setAllowNavSticky] = useState(true);
  const filterStripHideTimerRef = useRef<number | null>(null);

  useEffect(() => {
    if (filterStripHideTimerRef.current != null) {
      window.clearTimeout(filterStripHideTimerRef.current);
      filterStripHideTimerRef.current = null;
    }

    const applyAllowNavSticky = () => {
      if (!hideNavbarForFilter) {
        setAllowNavSticky(true);
        return;
      }
      if (typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        setAllowNavSticky(false);
        return;
      }
      setAllowNavSticky(true);
      filterStripHideTimerRef.current = window.setTimeout(() => {
        filterStripHideTimerRef.current = null;
        setAllowNavSticky(false);
      }, NAV_HIDE_FOR_FILTER_STRIP_MS);
    };

    const rafId = window.requestAnimationFrame(applyAllowNavSticky);
    return () => {
      window.cancelAnimationFrame(rafId);
      if (filterStripHideTimerRef.current != null) {
        window.clearTimeout(filterStripHideTimerRef.current);
        filterStripHideTimerRef.current = null;
      }
    };
  }, [hideNavbarForFilter]);

  const filterStripStickyEligible = !hideNavbarForFilter || allowNavSticky;
  const navIsSticky = isSticky && plpStickyEligible && filterStripStickyEligible;
  const navSlideUpForFilter = hideNavbarForFilter && allowNavSticky && isSticky;
  const { navbarBg, navbarLinkColor, headerTextColor, mainBarBg, layout: headerLayout } = useDesktopHeaderConfig();
  const navLayoutClass = headerLayoutWrapperClass(headerLayout);
  const navLinkStyle = { color: navbarLinkColor } as const;
  const rightAlignedTrailingMenuCount = desktopMenuData?.rightAlignedTrailingMenuCount ?? 3;
  const menuItems = menuLoading ? [] : desktopMenuData?.subData ?? [];
  const clampedRightAlignedCount = Math.max(0, Math.min(rightAlignedTrailingMenuCount, menuItems.length));
  const leftMenuItems = menuItems.slice(0, menuItems.length - clampedRightAlignedCount);
  const rightMenuItems = menuItems.slice(menuItems.length - clampedRightAlignedCount);
  const rightAlignedMenuIds = new Set(rightMenuItems.map((item) => item.id));
  const trackNavigationBarHovered = useTrackNavigationBarHovered();
  const trackNavigationBarClick = useTrackNavigationBarClick();

  const getSubmenuAlignment = (item: DesktopNavItem): 'left' | 'right' => {
    const preference = item.submenu?.positionPreference ?? 'auto';
    if (preference === 'left' || preference === 'right') return preference;
    return rightAlignedMenuIds.has(item.id) ? 'right' : 'left';
  };

  const renderSingleColumnMenu = (
    submenu: DesktopNavItem['submenu'],
    menuId: string,
    alignment: 'left' | 'right',
    menuParentLabel: string,
  ) => {
    if (!submenu || submenu.type !== 'single-column' || !submenu.items) return null;
    const isOffers = menuId === 'offers';

    return (
      <div
        className={`absolute ${alignment === 'right' ? 'right-0' : 'left-0'} top-full mt-0 bg-white border border-gray-200 shadow-lg rounded-b-md rounded-t-none min-w-[200px] z-50`}
        onMouseEnter={() => setHoveredMenu(menuId)}
        onMouseLeave={() => setHoveredMenu(null)}
      >
        <ul>
          {submenu.items.map((item, index) => {
            const itemLabel = item.title || item.label || '';
            const itemUrl = isOffers
              ? withLeadingSlash(item.link || item.url || '#')
              : item.link || item.url || '#';
            return (
              <li key={index}>
                <AppLink
                  to={itemUrl}
                  shouldRefresh={isOffers || (item.shouldRefresh ?? navShouldRefresh(itemUrl))}
                  className="block px-4 py-2.5 text-sm text-gray-700 hover:text-hover transition-colors"
                  {...navBarLinkTrack({
                    menuName: menuParentLabel,
                    buttonName: itemLabel,
                  })}
                >
                  {itemLabel}
                </AppLink>
                {index < submenu.items!.length - 1 && (
                  <div className="mx-4 border-b border-dotted border-gray-200" />
                )}
              </li>
            );
          })}
        </ul>
      </div>
    );
  };

  const renderMegaMenu = (
    submenu: DesktopNavItem['submenu'],
    menuId: string,
    alignment: 'left' | 'right',
    menuParentLabel: string,
  ) => {
    if (!submenu || submenu.type !== 'mega' || !submenu.columns) return null;

    const columnCount = submenu.columns.length;
    // For rings, earrings, and pendants, make first column wider
    const isWideFirstColumn = ['rings', 'earrings', 'pendants'].includes(menuId);
    // For gold-coins, use 2-column equal layout
    const isGoldCoins = menuId === 'gold-coins';
    // For all-jewellery, group columns into 4 visual columns
    const isAllJewellery = menuId === 'all-jewellery';
    const hasSilverCoins = submenu.columns.some((col) => col.title === 'Silver Coins');
    
    let gridStyle: React.CSSProperties | undefined;
    if (isWideFirstColumn && columnCount === 4) {
      // First column wider, last 3 columns equal width
      gridStyle = { gridTemplateColumns: '1.5fr 1fr 1fr 1.2fr' };
    } else if (isWideFirstColumn && columnCount === 3) {
      // First column wider, last 2 columns equal width
      gridStyle = { gridTemplateColumns: '1.5fr 1fr 1fr' };
    } else if (isWideFirstColumn && columnCount === 5) {
      // First column wider, last 4 columns equal width
      gridStyle = { gridTemplateColumns: '1.5fr 1fr 1fr 1fr 1fr' };
    } else if (isGoldCoins && columnCount === 2) {
      // Gold coins: 2 equal columns
      gridStyle = { gridTemplateColumns: '1fr 1fr' };
    }
    
    const gridCols = !isWideFirstColumn && !isGoldCoins && !isAllJewellery
      ? (columnCount === 2 ? 'grid-cols-2' : columnCount === 3 ? 'grid-cols-3' : columnCount === 4 ? 'grid-cols-4' : columnCount === 5 ? 'grid-cols-5' : 'grid-cols-4')
      : isAllJewellery ? 'grid-cols-4' : '';

    // Group columns for all-jewellery menu
    const groupedColumns = isAllJewellery && submenu.columns.length >= 8
      ? [
          [submenu.columns[0]], // Column 1: Shop By Category
          [submenu.columns[1], submenu.columns[2], submenu.columns[3]], // Column 2: Men's, Kids, Platinum
          [submenu.columns[4], submenu.columns[5]], // Column 3: Gold, Gemstone
          [submenu.columns[6], submenu.columns[7]], // Column 4: Diamond, Pearl
        ]
      : null;

    return (
      <div
        className={`${isGoldCoins ? (hasSilverCoins ? 'w-[500px] right-0' : 'w-[400px] right-0') : `${alignment === 'right' ? 'right-0' : 'left-0'} w-full max-w-[1200px]`} absolute top-full mt-0 bg-white shadow-xl rounded-b-md rounded-t-none z-50`}
        onMouseEnter={() => setHoveredMenu(menuId)}
        onMouseLeave={() => setHoveredMenu(null)}
      >
        <div className={`grid ${gridCols}`} style={gridStyle}>
          {isAllJewellery && groupedColumns
            ? groupedColumns.map((group, groupIndex) => {
                const isLastGroup = groupIndex === groupedColumns.length - 1;
                return (
                  <div
                    key={groupIndex}
                    className={`flex flex-col py-6 px-6 ${!isLastGroup ? 'border-r border-[#ececec]' : ''}`}
                  >
                    {group.map((column, sectionIndex) => {
                      const isTwoColumnSection = ['Men\'s Jewellery', 'Kids Jewellery', 'Platinum Jewellery'].includes(column.title || '');
                      return (
                        <div key={sectionIndex} className={sectionIndex > 0 ? 'mt-6' : ''}>
                          {column.title && (
                            <h3 className="text-sm font-semibold leading-normal mb-3 pb-2 capitalize text-[#002c5c] border-b border-dashed border-[#e5e5e5]">
                              {column.title}
                            </h3>
                          )}
                          {column.items && (
                            <ul className={isTwoColumnSection ? 'grid grid-cols-2 gap-x-4 gap-y-2' : 'space-y-2'}>
                              {(column.items as DesktopNavColumnItem[]).map((item, itemIndex) => (
                                <li key={itemIndex}>
                                  <AppLink
                                    to={item.url}
                                    shouldRefresh={item.shouldRefresh ?? navShouldRefresh(item.url)}
                                    className="block text-sm text-gray-700 hover:text-hover transition-colors"
                                    {...navBarLinkTrack({
                                      menuName: menuParentLabel,
                                      categoryName: column.title,
                                      buttonName: item.label,
                                    })}
                                  >
                                    <div className="flex items-center gap-2">
                                      <span>{item.label}</span>
                                      {item.tag && (
                                        <span className="text-[10px] bg-orange-500 text-white px-[3px] py-[0px] rounded font-semibold">
                                          {item.tag}
                                        </span>
                                      )}
                                    </div>
                                  </AppLink>
                                </li>
                              ))}
                            </ul>
                          )}
                          {column.buttonText && column.buttonUrl && (
                            <div className="mt-3 flex justify-end">
                              <AppLink
                                to={column.buttonUrl}
                                shouldRefresh={column.shouldRefresh ?? navShouldRefresh(column.buttonUrl)}
                                className="text-sm text-[#002c5c] hover:text-hover transition-colors flex items-center gap-1"
                                {...navBarLinkTrack({
                                  menuName: menuParentLabel,
                                  categoryName: column.title,
                                  buttonName: column.buttonText ?? '',
                                })}
                              >
                                <span>{column.buttonText}</span>
                                <span>▸</span>
                              </AppLink>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                );
              })
            : submenu.columns.map((column, colIndex) => {
            // Check if this is a collection-style column (has items with imageUrl)
            const isCollectionColumn = column.items && !isGoldCoins && (column.items as DesktopNavColumnItem[]).some(item => item.imageUrl);
            const isLastColumn = colIndex === columnCount - 1;
            
            return (
              <div
                key={colIndex}
                className={`flex py-6 px-6 flex-col ${colIndex === 1 && !isGoldCoins ? 'bg-[#f9f9f9]' : ''} ${isLastColumn ? 'border-l border-[#f9f9f9]' : ''} relative`}
              >
                {/* Collection UI: Title and View All on same line */}
                {isCollectionColumn ? (
                  <div className="flex items-center justify-between mb-4">
                    {column.title && !(menuId === 'gifts' && isLastColumn) && (
                      <h3 className="text-sm font-semibold leading-normal py-[4px] pb-[7px] capitalize text-[#002c5c] mr-6">
                        {column.title}
                      </h3>
                    )}
                    {column.buttonText && column.buttonUrl && (
                      <AppLink
                        to={column.buttonUrl}
                        shouldRefresh={column.shouldRefresh ?? navShouldRefresh(column.buttonUrl)}
                        eventName={BROWSE_ALL_COLLECTIONS_CLICKED}
                        eventProperties={browseAllCollectionsClickedEventProperties({
                          fromSource: BROWSE_ALL_COLLECTIONS_FROM_SOURCE_NAV_BAR,
                          category:
                            menuParentLabel.trim() || formatNavMenuIdForCategory(menuId),
                        })}
                        onClick={() =>
                          trackNavigationBarClick({
                            menuName: menuParentLabel,
                            categoryName: column.title ?? '',
                            buttonName: column.buttonText ?? '',
                          })
                        }
                        className="group/view-all flex items-center gap-1.5 text-sm font-semibold text-[#002c5c] hover:text-hover transition-all duration-200 whitespace-nowrap border-b border-transparent hover:border-hover"
                      >
                        <span>{column.buttonText}</span>
                        <ArrowRight 
                          className="h-3.5 w-3.5 transition-transform duration-200 group-hover/view-all:translate-x-1" 
                          strokeWidth={2.5}
                        />
                      </AppLink>
                    )}
                  </div>
                ) : (
                  // Regular column title (hide for gifts menu last column)
                  column.title && !(menuId === 'gifts' && isLastColumn) && (
                    <h3
                      className={`text-sm font-semibold leading-normal ${isGoldCoins ? '' : 'mb-4'} py-[4px] pb-[7px] capitalize text-[#002c5c] mr-6 ${
                        isGoldCoins ? '' : 'border-b border-dashed border-[#e5e5e5]'
                      }`}
                    >
                      {column.title}
                    </h3>
                  )
                )}
              
                {/* Collection Banner */}
                {isCollectionColumn && column.items && (
                  <div className="flex flex-col gap-4 mb-4">
                    {(column.items as DesktopNavColumnItem[]).filter((item) => item.imageUrl).map((item, itemIndex) => (
                        <AnalyticsBannerLink
                          key={itemIndex}
                          to={item.url}
                          bannerName={item.label || column.title || 'Nav Collection Banner'}
                          bannerType="collections"
                          sectionName="DesktopNavbar"
                          fromNavBar={true}
                          className="relative block group"
                          onClick={() =>
                            trackNavigationBarClick({
                              menuName: menuParentLabel,
                              categoryName: column.title ?? '',
                              buttonName: item.label,
                            })
                          }
                        >
                          <div className="relative flex rounded-md shadow-[0_4px_6px_-1px_rgba(0,0,0,0.1),0_2px_4px_-1px_rgba(0,0,0,0.06)] overflow-hidden">
                            <img
                              src={item.imageUrl}
                              alt={item.label}
                              className="w-full h-full object-cover group-hover:opacity-90 transition-opacity"
                            />
                          </div>
                        </AnalyticsBannerLink>
                      ))}
                  </div>
                )}
              
                {/* Regular items list (if not collection column or if there are additional items) */}
                {column.items && !isCollectionColumn && (
                  <div className="flex-1">
                    {isGoldCoins ? (
                      <div className={`grid gap-6 ${colIndex === 0 ? 'grid-cols-2' : 'grid-cols-1'}`}>
                        {(column.items as DesktopNavSubGroup[]).map((subGroup, subGroupIndex) => (
                          <div key={subGroupIndex} className="flex flex-col">
                            <h4 className="text-sm font-semibold text-[#002D5F] mb-3 pb-2">
                              {subGroup.title}
                            </h4>
                            <ul className="space-y-2">
                              {subGroup.items?.map((item, itemIndex) => (
                                <li key={itemIndex}>
                                  <AppLink
                                    to={item.url || ''}
                                    shouldRefresh={item.shouldRefresh ?? navShouldRefresh(item.url || '')}
                                    className="block text-sm text-gray-700 hover:text-hover transition-colors"
                                    {...navBarLinkTrack({
                                      menuName: menuParentLabel,
                                      categoryName: `${column.title} - ${subGroup.title}`,
                                      buttonName: item.label || '',
                                    })}
                                  >
                                    {item.label}
                                  </AppLink>
                                </li>
                              ))}
                            </ul>
                          </div>
                        ))}
                      </div>
                    ) : (column.items as DesktopNavColumnItem[]).length > 10 ? (
                      <ul className="grid grid-cols-2 gap-x-4 gap-y-2">
                        {(column.items as DesktopNavColumnItem[]).map((item, itemIndex) => (
                          <li key={itemIndex}>
                            <AppLink
                              to={item.url}
                              shouldRefresh={item.shouldRefresh ?? navShouldRefresh(item.url)}
                              className="block text-sm text-gray-700 hover:text-hover transition-colors group"
                              {...navBarLinkTrack({
                                menuName: menuParentLabel,
                                categoryName: column.title,
                                buttonName: item.label,
                              })}
                            >
                              <div className="flex items-center gap-2">
                                <span>{item.label}</span>
                                {item.tag && (
                                  <span className="text-xs bg-red-500 text-white px-1.5 py-0.5 rounded">
                                    {item.tag}
                                  </span>
                                )}
                              </div>
                              {item.description && (
                                <span className="text-xs text-gray-500 block mt-0.5">
                                  {item.description}
                                </span>
                              )}
                            </AppLink>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <ul className="space-y-2">
                        {(column.items as DesktopNavColumnItem[]).map((item, itemIndex) => (
                          <li key={itemIndex}>
                            <AppLink
                              to={item.url}
                              shouldRefresh={item.shouldRefresh ?? navShouldRefresh(item.url)}
                              className="block text-sm text-gray-700 hover:text-hover transition-colors"
                              {...navBarLinkTrack({
                                menuName: menuParentLabel,
                                categoryName: column.title,
                                buttonName: item.label,
                              })}
                            >
                              <div className="flex items-center gap-2">
                                <span>{item.label}</span>
                                {item.tag && (
                                  <span className="text-xs bg-red-500 text-white px-1.5 py-0.5 rounded">
                                    {item.tag}
                                  </span>
                                )}
                              </div>
                              {item.description && (
                                <span className="text-xs text-gray-500 block mt-0.5">
                                  {item.description}
                                </span>
                              )}
                            </AppLink>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}

                {/* MicroBanner (for columns with microBanner but not collection style) */}
                {column.microBanner && !isCollectionColumn && (
                  <div className="mt-4 pt-4 border-t border-[#f9f9f9] absolute bottom-0 left-0 right-0 w-full">
                    <AppLink 
                      to={column.microBanner.url}
                      shouldRefresh={column.microBanner.shouldRefresh ?? navShouldRefresh(column.microBanner.url)}
                      className="block group bg-white rounded-md overflow-hidden"
                      {...navBarLinkTrack({
                        menuName: menuParentLabel,
                        categoryName: column.title,
                        buttonName: column.microBanner.title,
                      })}
                    >
                      <div className="flex items-end justify-end min-h-[100px] relative">
                        {/* Image on right */}
                        <div className="flex-shrink-0 overflow-hidden">
                          <img
                            src={column.microBanner.imageUrl}
                            alt={column.microBanner.title}
                            className="w-full h-full object-cover object-right group-hover:opacity-90 transition-opacity"
                          />
                        </div>
                        {/* Text on left */}
                        <div className="flex-1 px-5 py-4 absolute top-0 left-0">
                          <div className="text-[13px] font-semibold text-gray-800 mb-2 leading-tight">
                            {column.microBanner.title}
                          </div>
                          <div className="text-[11px] text-gray-800 font-normal leading-tight">
                            {column.microBanner.description}
                          </div>
                        </div>
                        
                        
                      </div>
                    </AppLink>
                  </div>
                )}

                {/* Regular button (for non-collection columns) */}
                {column.buttonText && column.buttonUrl && !isCollectionColumn && (
                  <div className="mt-4 pt-4 border-t border-gray-200">
                    <Button
                      variant="outline"
                      size="sm"
                      asChild
                      className="w-full text-xs"
                    >
                      <AppLink
                        to={column.buttonUrl}
                        shouldRefresh={column.shouldRefresh ?? navShouldRefresh(column.buttonUrl)}
                        {...navBarLinkTrack({
                          menuName: menuParentLabel,
                          categoryName: column.title,
                          buttonName: column.buttonText ?? '',
                        })}
                      >
                        {column.buttonText}
                      </AppLink>
                    </Button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const renderSolitaireMenu = (
    submenu: DesktopNavItem['submenu'],
    menuId: string,
    alignment: 'left' | 'right',
    menuParentLabel: string,
  ) => {
    if (!submenu || submenu.type !== 'solitaire' || !submenu.sections) return null;

    const sections = submenu.sections;

    return (
      <div
        className={`absolute top-full mt-0 ${alignment === 'right' ? 'right-0' : 'left-0'} w-full max-w-[1200px] bg-white border border-gray-200 shadow-xl rounded-b-md rounded-t-none z-50`}
        onMouseEnter={() => setHoveredMenu(menuId)}
        onMouseLeave={() => setHoveredMenu(null)}
      >
        <div className="grid grid-cols-4">
          {sections.map((section, sectionIndex) => {
            const isLastColumn = sectionIndex === sections.length - 1;
            const isEducationColumn = !section.imageUrl && section.items;
            
            return (
              <div key={sectionIndex} className={`flex flex-col py-6 px-6 ${!isLastColumn ? 'border-r border-[#ececec]' : ''}`}>
                {section.title && (
                  <h3 className={`text-sm font-semibold text-[#002c5c] mb-3 pb-2 border-b border-dashed border-[#e5e5e5] ${isLastColumn && isEducationColumn ? 'text-lg font-bold' : ''}`}>
                    {section.title}
                  </h3>
                )}

                {section.imageUrl && section.url && (
                  <div className="mb-4 max-w-[250px]">
                    <AppLink
                      to={section.url}
                      shouldRefresh={section.shouldRefresh ?? navShouldRefresh(section.url)}
                      className="block group"
                      {...navBarLinkTrack({
                        menuName: menuParentLabel,
                        categoryName: section.title,
                        buttonName: section.label ?? section.title ?? '',
                      })}
                    >
                      <img
                        src={section.imageUrl}
                        alt={section.title}
                        className="w-full h-auto mb-3 group-hover:opacity-90 transition-opacity"
                      />
                      {section.label && (
                        <Button variant="outline" size="sm" asChild className="w-full h-auto whitespace-normal text-center leading-tight py-2">
                          <AppLink
                            to={section.url}
                            shouldRefresh={section.shouldRefresh ?? navShouldRefresh(section.url)}
                            {...navBarLinkTrack({
                              menuName: menuParentLabel,
                              categoryName: section.title,
                              buttonName: section.label,
                            })}
                          >
                            {section.label}
                          </AppLink>
                        </Button>
                      )}
                      {section.label && section.buttonText && (
                        <div className="text-center text-gray-600 text-xs my-2 lg:my-4">-- or --</div>
                      )}
                      {section.buttonText && section.buttonUrl && (
                        <AppLink
                          to={section.buttonUrl}
                          shouldRefresh={section.shouldRefresh ?? navShouldRefresh(section.buttonUrl)}
                          className="group/view-all block text-xs lg:text-sm font-semibold text-[#002c5c] hover:text-hover transition-all duration-200 border border-[#002c5c] hover:border-hover rounded-md py-2 px-2 lg:px-4 mt-2 text-center leading-tight"
                          {...navBarLinkTrack({
                            menuName: menuParentLabel,
                            categoryName: section.title,
                            buttonName: section.buttonText,
                          })}
                        >
                          {section.buttonText}
                          <ArrowRight 
                            className="inline-block h-3.5 w-3.5 ml-1 align-middle transition-transform duration-200 group-hover/view-all:translate-x-1" 
                            strokeWidth={2.5}
                          />
                        </AppLink>
                      )}
                    </AppLink>
                  </div>
                )}

                {/* Last column (Diamond Education) styling */}
                {isLastColumn && isEducationColumn && (
                  <>
                    {section.label && section.url && (
                      <AppLink
                        to={section.url}
                        shouldRefresh={section.shouldRefresh ?? navShouldRefresh(section.url)}
                        className="block mb-4"
                        {...navBarLinkTrack({
                          menuName: menuParentLabel,
                          categoryName: section.title,
                          buttonName: section.label,
                        })}
                      >
                        <div className="bg-gray-100 hover:bg-gray-200 rounded-md py-2.5 px-4 text-sm font-medium text-gray-700 transition-colors">
                          {section.label}
                        </div>
                      </AppLink>
                    )}
                    {section.items && (
                      <ul className="grid grid-cols-1 xl:grid-cols-2 gap-x-4 gap-y-3">
                        {section.items.map((item, itemIndex) => {
                          const iconSrc = diamondEducationIcons[item.label];
                          return (
                            <li key={itemIndex}>
                              <AppLink
                                to={item.url}
                                shouldRefresh={item.shouldRefresh ?? navShouldRefresh(item.url)}
                                className="flex items-center gap-2 lg:gap-3 group"
                                {...navBarLinkTrack({
                                  menuName: menuParentLabel,
                                  categoryName: section.title,
                                  buttonName: item.label,
                                })}
                              >
                                {/* Icon */}
                                <div className="flex-shrink-0 w-10 h-10 max-w-[64px] max-h-[64px] rounded-full bg-[#fcefe5] flex items-center justify-center group-hover:bg-[#f9e5d8] transition-colors">
                                  {iconSrc ? (
                                    <img src={iconSrc} alt={item.label} className="w-5 h-5 max-w-[24px] max-h-[24px]" style={{ filter: 'invert(50%) sepia(78%) saturate(632%) hue-rotate(324deg) brightness(92%) contrast(90%)' }} />
                                  ) : (
                                    <div className="w-4 h-4 lg:w-5 lg:h-5 rounded-full bg-[#e06754]/30 flex items-center justify-center">
                                      <div className="w-1.5 h-1.5 lg:w-2 lg:h-2 rounded-full bg-[#e06754]"></div>
                                    </div>
                                  )}
                                </div>
                                {/* Text label */}
                                <span className="text-xs lg:text-sm font-medium text-[#337ab7] hover:text-orange-500 cursor-pointer transition-colors">
                                  {item.label}
                                </span>
                              </AppLink>
                            </li>
                          );
                        })}
                      </ul>
                    )}
                  </>
                )}

                {/* Regular items list for non-education columns */}
                {section.items && !(isLastColumn && isEducationColumn) && (
                  <ul className="space-y-2">
                    {section.items.map((item, itemIndex) => (
                      <li key={itemIndex}>
                        <AppLink
                          to={item.url}
                          shouldRefresh={item.shouldRefresh ?? navShouldRefresh(item.url)}
                          className="block text-sm text-gray-700 hover:text-hover transition-colors"
                          {...navBarLinkTrack({
                            menuName: menuParentLabel,
                            categoryName: section.title,
                            buttonName: item.label,
                          })}
                        >
                          {item.label}
                        </AppLink>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const renderSubmenu = (item: DesktopNavItem) => {
    if (!item.submenu) return null;
    const alignment = getSubmenuAlignment(item);

    switch (item.submenu.type) {
      case 'single-column':
        return renderSingleColumnMenu(item.submenu, item.id, alignment, item.label);
      case 'mega':
        return renderMegaMenu(item.submenu, item.id, alignment, item.label);
      case 'solitaire':
        return renderSolitaireMenu(item.submenu, item.id, alignment, item.label);
      default:
        return null;
    }
  };

  const renderTopMenuItem = (item: DesktopNavItem) => {
    // Only show dropdown carat/div when submenu exists and has items (e.g. offers with empty items → no dropdown)
    const hasSubmenuItems = item.submenu && (
      item.submenu.type === 'single-column'
        ? item.submenu.items != null && item.submenu.items.length > 0
        : item.submenu.type === 'mega' || item.submenu.type === 'solitaire'
          ? true
          : false
    );
    const isHovered = hoveredMenu === item.id;
    const isGoldCoins = item.id === 'gold-coins';

    return (
      <li
        key={item.id}
        className="relative flex h-full flex-shrink-0 items-center"
        onMouseEnter={() => {
          trackNavigationBarHovered(item.label);
          if (hasSubmenuItems) setHoveredMenu(item.id);
        }}
        onMouseLeave={() => setHoveredMenu(null)}
      >
        {item.url ? (
          <AppLink
            to={item.url}
            shouldRefresh={navShouldRefresh(item.url)}
            className="group flex h-full items-center gap-1 whitespace-nowrap px-[3px] text-xs font-semibold tracking-[0.075em] transition-opacity hover:opacity-75 focus:opacity-75 sm:px-[4px] lg:px-2"
            style={navLinkStyle}
            {...navBarLinkTrack({
              menuName: item.label,
              buttonName: item.label,
            })}
          >
            {item.label}
            {hasSubmenuItems && (
              <ChevronDown
                className="h-3 w-3 flex-shrink-0 transition-transform hidden xl:block"
                style={navLinkStyle}
              />
            )}
          </AppLink>
        ) : (
          <span
            className="group flex h-full cursor-pointer items-center gap-1 whitespace-nowrap px-[3px] text-xs font-semibold tracking-[0.1em] sm:px-[4px] lg:px-2"
            style={navLinkStyle}
          >
            {item.label}
            {hasSubmenuItems && (
              <ChevronDown
                className="h-3 w-3 flex-shrink-0 transition-transform hidden xl:block"
                style={navLinkStyle}
              />
            )}
          </span>
        )}

        {/* White caret indicator (upward triangle) at bottom of blue navbar */}
        {isHovered && (
          <span
            className="pointer-events-none absolute left-1/2 -translate-x-1/2 bottom-0 w-0 h-0 border-l-[6px] border-r-[6px] border-b-[6px] border-l-transparent border-r-transparent border-b-white z-50"
            aria-hidden="true"
          />
        )}

        {hasSubmenuItems && isHovered && (
          <>
            {item.submenu?.type === 'single-column' && renderSubmenu(item)}
            {item.submenu?.type === 'mega' && isGoldCoins && renderSubmenu(item)}
          </>
        )}
      </li>
    );
  };

  return (
    <>
      {/* Sticky trigger sentinel (must be immediately before the target element) */}
      <div ref={sentinelRef} className="h-0" aria-hidden />
      <nav
        className={cn(
          'group z-[40] mt-0 h-12 w-full transform-gpu will-change-transform',
          navIsSticky && 'nav-sticky sticky',
          navSlideUpForFilter &&
            '-translate-y-full pointer-events-none motion-reduce:-translate-y-0 motion-reduce:pointer-events-auto',
          isNewHomeRecommendedBrowse &&
            'motion-reduce:transition-none transition-transform duration-[380ms] ease-[cubic-bezier(0.32,0.72,0,1)]',
          className,
        )}
        style={{
          backgroundColor: navbarBg,
          // When the desktop header is fixed above (scroll-up mode), --nh-sticky-header-h
          // is set to the header height so the navbar sticks just below it.
          // Defaults to 0px (no offset) when the header is not fixed.
          top: navIsSticky ? 'var(--nh-sticky-header-h, 0px)' : undefined,
          // Transition `top` in sync with the header slide-in animation (0.28s).
          transition: navIsSticky ? 'top 0.28s ease-out' : undefined,
        }}
      >
        <div
          className="logo nav-logo-badge w-[83px] aspect-square rounded-full flex items-center justify-center absolute left-[12px] z-10 translate-y-[-76px] p-4 transform-gpu will-change-transform transition-[transform,width,left,padding] duration-500 ease-in-out group-[.nav-sticky]:w-11 group-[.nav-sticky]:p-1.5 group-[.nav-sticky]:translate-y-0.5 group-[.nav-sticky]:left-[40px]"
          style={{ backgroundColor: mainBarBg }}
        >
          <AppLink
            to="/"
            shouldRefresh={false}
            eventName="BlueStone Logo Clicked"
            aria-label="BlueStone Logo"
            className="block h-full w-full"
          >
            <BsLogoSymbolIcon color={headerTextColor} />
          </AppLink>
        </div>
      <div className={`${navLayoutClass} relative`}>
        <div className="flex h-12 items-center justify-between pl-[70px]">
          <ul className="flex h-full flex-nowrap items-center gap-0">
            {leftMenuItems.map(renderTopMenuItem)}
          </ul>
          {rightMenuItems.length > 0 && (
            <ul className="ml-4 flex h-full flex-nowrap items-center gap-0">
              {rightMenuItems.map(renderTopMenuItem)}
            </ul>
          )}
        </div>
        {/* Render mega menus at container level for full width (except gold-coins) */}
        {menuItems.map((item) => {
          if (item.submenu && (item.submenu.type === 'mega' || item.submenu.type === 'solitaire') && hoveredMenu === item.id && item.id !== 'gold-coins') {
            return (
              <div key={`mega-${item.id}`}>{renderSubmenu(item)}</div>
            );
          }
          return null;
        })}
      </div>
      </nav>
    </>
  );
}
